import moment from 'moment-timezone'

// Constants
const BUSINESS_HOURS = {
  START: 9, // 9 AM
  END: 20,  // 8 PM
}

// Mock data for testing
const MOCK_EXTENSIONS = [
  { id: 947, friendly_extension: '444 Mailbox' }
]

// Sample contact list with phone numbers and caller ID names
const MOCK_CONTACTS = [
  { name: 'Foghorn Leghorn', phone: '+14045554567', cnam: 'LEGHORN,FOGHORN' },
  { name: 'Wade Wings', phone: '+14045557890', cnam: 'WINGS,WADE' },
  { name: 'Cluck Norris', phone: '+14045558765', cnam: 'NORRIS,CLUCK' },
  { name: 'Feather Locklear', phone: '+14045552468', cnam: 'WIRELESS CALLER' },
  { name: 'Benedict Eggs', phone: '+14045551357', cnam: 'EGGS,BENEDICT' },
  { name: 'Yolko Ono', phone: '+14045559135', cnam: 'ONO,YOLKO' },
  { name: 'Rooster Cogburn', phone: '+14045553579', cnam: 'COGBURN,ROOSTER' },
  { name: 'Coop DeVille', phone: '+14045557531', cnam: 'DEVILLE,COOP' },
  { name: 'Hen Solo', phone: '+14045556789', cnam: 'SOLO,HEN' },
  { name: 'Sherlock Combs', phone: '+14045553456', cnam: 'COMBS,SHERLOCK' },
  { name: 'Peck Peckham', phone: '+14045559999', cnam: 'PECKHAM,PECK' },
  { name: 'Wingston Churchill', phone: '+14045551111', cnam: 'CHURCHILL,WINGSTON' },
  { name: 'Chick Jagger', phone: '+14045553333', cnam: 'JAGGER,CHICK' },
  { name: 'Talon Swift', phone: '+14045554444', cnam: 'SWIFT,TALON' },
  { name: 'Poultry Deschanel', phone: '+14045555555', cnam: 'WIRELESS CALLER' },
  { name: 'Chickira', phone: '+14045556666', cnam: 'WIRELESS CALLER' },
  { name: 'Roost Gable', phone: '+14045558888', cnam: 'GABLE,ROOST' },
  { name: 'Roostopher Walken', phone: '+14045552233', cnam: 'WALKEN,ROOSTOPHER' },
  { name: 'Peckasso', phone: '+14045553344', cnam: 'WIRELESS CALLER' },
  { name: 'Henny Kravitz', phone: '+14045554455', cnam: 'KRAVITZ,HENNY' },
  { name: 'Fowl McCartney', phone: '+14045555566', cnam: 'MCCARTNEY,FOWL' },
  { name: 'Cluckminster Fuller', phone: '+14045557788', cnam: 'FULLER,CLUCKMINSTER' },
  { name: 'Hen Affleck', phone: '+14045558899', cnam: 'AFFLECK,HEN' },
  { name: 'Chickolás Cage', phone: '+14045551213', cnam: 'CAGE,CHICKOLAS' },
  { name: 'Wingona Ryder', phone: '+14045551415', cnam: 'RYDER,WINGONA' },
  { name: 'Beakoncé', phone: '+14045551617', cnam: 'WIRELESS CALLER' },
  { name: 'Morgan Freebird', phone: '+14045551819', cnam: 'FREEBIRD,MORGAN' },
  { name: 'Chick Van Dyke', phone: '+14045552425', cnam: 'VANDYKE,CHICK' },
  { name: 'Hengelina Jolie', phone: '+14045553031', cnam: 'JOLIE,HENGELINA' },
  { name: 'Coop Dogg', phone: '+14045553233', cnam: 'WIRELESS CALLER' }
]

/**
 * Generates a random minute within business hours
 * @returns {number} Minute of the day (between business hours)
 */
const getRandomBusinessMinute = () => {
  const totalMinutes = (BUSINESS_HOURS.END - BUSINESS_HOURS.START) * 60
  const randomMinute = Math.floor(Math.random() * totalMinutes)
  return (BUSINESS_HOURS.START * 60) + randomMinute
}

/**
 * Creates a single call record
 * @param {number} id - Unique identifier for the call
 * @param {string} date - Date of the call
 * @param {number} minuteOfDay - Minute of the day when call occurred
 * @param {Object} options - Optional parameters for call properties
 * @returns {Object} Call record object
 */
const createCallRecord = (id, date, minuteOfDay, options = {}) => {
  const contact = MOCK_CONTACTS[Math.floor(Math.random() * MOCK_CONTACTS.length)]
  const direction = options.direction || (Math.random() > 0.5 ? 'inbound' : 'outbound')
  const hasCallStart = 'hasCallStart' in options ? options.hasCallStart : (Math.random() > 0.3)
  
  const callTime = moment(date)
    .hours(Math.floor(minuteOfDay / 60))
    .minutes(minuteOfDay % 60)
  
  return {
    // Call identification
    id,
    direction,
    phone: contact.phone,
    name: contact.name,
    cnam: contact.cnam,
    
    // Call timing
    ring_start: callTime.toISOString(),
    call_start: hasCallStart ? callTime.toISOString() : null,
    call_end: callTime.clone().add(hasCallStart ? 1 : 0, 'minutes').toISOString(),
    duration: hasCallStart ? '00:01:00' : null,
    
    // Extension info
    extension_id: MOCK_EXTENSIONS[0].id,
    friendly_extension: MOCK_EXTENSIONS[0].friendly_extension,
    
    // Formatted display values
    formattedDate: callTime.format('MMM DD, YYYY'),
    formattedTime: callTime.format('h:mm a'),
    
    // Voicemail status (only for inbound missed calls)
    voicemail: direction === 'inbound' && !hasCallStart ? 
      ('hasVoicemail' in options ? options.hasVoicemail : Math.random() > 0.5) : null
  }
}

/**
 * Generates mock call data for the last 7 days
 * @returns {Object} Dictionary of dates containing array of call records
 */
export const getMockCallData = () => {
  const mockData = {}
  let currentId = 1
  
  // Generate data for last 7 days
  for (let dayOffset = 0; dayOffset < 7; dayOffset++) {
    const date = moment().subtract(dayOffset, 'days').format('YYYY-MM-DD')
    mockData[date] = []
    
    // First day: Generate one of each call type
    if (dayOffset === 0) {
      const guaranteedCalls = [
        { direction: 'inbound', hasCallStart: false, hasVoicemail: true },  // Missed with voicemail
        { direction: 'inbound', hasCallStart: true },                       // Answered inbound
        { direction: 'outbound', hasCallStart: true },                      // Completed outbound
        { direction: 'outbound', hasCallStart: false }                      // Failed outbound
      ]
      
      guaranteedCalls.forEach(options => {
        mockData[date].push(createCallRecord(currentId++, date, getRandomBusinessMinute(), options))
      })
    }
    
    // Add 1-4 random calls for each day
    const additionalCalls = Math.floor(Math.random() * 4) + 1
    for (let i = 0; i < additionalCalls; i++) {
      mockData[date].push(createCallRecord(currentId++, date, getRandomBusinessMinute()))
    }
    
    // Sort calls by ring_start (newest first)
    mockData[date].sort((a, b) => moment(b.ring_start).diff(moment(a.ring_start)))
  }
  
  return mockData
}



// WEBPACK FOOTER //
// ./src/data/mockCallData.js